import {
  FormControl,
  FormLabel,
  Autocomplete,
  TextField,
  Typography,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AutcompleteCupsItemBlockComponent from "../UI/AutocompleteCupsItemBlockComponent/AutcompleteCupsItemBlockComponent";
import { OwnerDataCups } from "../../core/models/ownerData/ownerData.model";
import { useState } from "react";
import { initialOwnerDataCup } from "../../utils/ownerData/ownerData.util";
import "./ConsumptionsManagementsDeliveryAddressComponent.scss";

interface ManagementsDeliveryAddressComponentProps {
  currentCup?: OwnerDataCups | undefined;
  ownerDataCups: OwnerDataCups[];
  ownerDataCupSelected: OwnerDataCups;
  setOwnerDataCupSelected: (ownerDataCupSelected: OwnerDataCups) => void;
  excludeGas?: boolean;
  excludeElectricity?: boolean;
}

const ConsumptionsManagementsDeliveryAddressComponent = ({
  currentCup,
  ownerDataCups,
  ownerDataCupSelected,
  setOwnerDataCupSelected,
  excludeGas,
  excludeElectricity,
}: ManagementsDeliveryAddressComponentProps) => {
  const { t } = useTranslation();
  const [cupsCodeValue, setCupsCodeValue] = useState<string>("");
  const hasCodeCups = currentCup
    ? !!currentCup.codeCups.length
    : !!ownerDataCupSelected?.codeCups.length;

  const handleFilterOptions = (options: OwnerDataCups[]) => {
    if (excludeGas) {
      const filteredOptions = options.filter(
        (option) => option.envirorment !== "Gas"
      );
      const value = currentCup ? "" : cupsCodeValue.trim().toLowerCase();

      if (value.length > 2) {
        return filteredOptions.filter(({ codeCups }) =>
          codeCups.toLowerCase().includes(value)
        );
      }

      return filteredOptions;
    } else if (excludeElectricity) {
      const filteredOptions = options.filter(
        (option) => option.envirorment !== "Luz"
      );
      const value = currentCup ? "" : cupsCodeValue.trim().toLowerCase();

      if (value.length > 2) {
        return filteredOptions.filter(({ codeCups }) =>
          codeCups.toLowerCase().includes(value)
        );
      }

      return filteredOptions;
    } else {
      const value = currentCup ? "" : cupsCodeValue.trim().toLowerCase();

      if (value.length > 2) {
        return options.filter(({ codeCups }) =>
          codeCups.toLowerCase().includes(value.toLowerCase())
        );
      }
      return options;
    }
  };

  const handleChange = (
    event: React.SyntheticEvent<Element, Event>,
    ownerDataCup: OwnerDataCups[],
    reason: AutocompleteChangeReason
  ) => {
    if (reason !== "removeOption") {
      setOwnerDataCupSelected(
        ownerDataCup.length > 1 ? ownerDataCup[1] : ownerDataCup[0]
      );
    }
  };

  const handleInputChange = (
    event: React.SyntheticEvent<Element, Event>,
    newValue: string,
    reason: AutocompleteInputChangeReason
  ) => {
    if (hasCodeCups) {
      return setCupsCodeValue("");
    }

    if (reason !== "reset") {
      return setCupsCodeValue(newValue);
    }
  };

  return (
    <FormControl
      fullWidth
      required
      sx={{
        display: "flex",
        gap: "1rem",
      }}
    >
      <FormLabel htmlFor="cupCif" required>
        <Typography fontWeight={600}>
          {t("Dirección de suministro")}
          <span style={{ color: "red" }}> *</span>
        </Typography>
      </FormLabel>
      <FormControl>
        <Autocomplete
          options={ownerDataCups}
          inputValue={cupsCodeValue}
          onInputChange={handleInputChange}
          filterOptions={handleFilterOptions}
          value={
            currentCup
              ? [currentCup]
              : hasCodeCups
              ? [ownerDataCupSelected]
              : []
          }
          autoComplete={cupsCodeValue.length > 2 ? true : false}
          getOptionLabel={(option) => option.codeCups}
          multiple={true}
          sx={{ width: "100%", ...(currentCup && { pointerEvents: "none" }) }}
          noOptionsText={t("Tablas.No hay coincidencias")}
          onKeyDown={(event) => hasCodeCups && event.preventDefault()}
          onChange={handleChange}
          renderOption={(props, ownerDataCup, { selected }) => {
            return (
              <li
                {...props}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <AutcompleteCupsItemBlockComponent
                  ownerDataCup={ownerDataCup}
                />
                {selected && <span className="t-icon-check" />}
              </li>
            );
          }}
          renderTags={(ownerDataCups) =>
            ownerDataCups.map((ownerDataCup) => (
              <AutcompleteCupsItemBlockComponent
                key={ownerDataCup.idCups}
                ownerDataCup={ownerDataCup}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              className="consumptions_address-cups"
              sx={{
                width: "100%",
                caretColor: hasCodeCups ? "transparent" : "auto",
              }}
              placeholder={hasCodeCups ? "" : t(`Consumos.Seleccionar`)}
              inputProps={{
                ...params.inputProps,
                style: {
                  boxShadow: "none",
                  display: hasCodeCups ? "none" : "inline",
                },
              }}
              InputProps={{
                ...params.InputProps,
                fullWidth: true,
                endAdornment: (
                  <>
                    {!!hasCodeCups && !currentCup ? (
                      <IconButton
                        sx={{ position: "absolute", right: "16px" }}
                        size="small"
                        onClick={() =>
                          setOwnerDataCupSelected(initialOwnerDataCup)
                        }
                      >
                        <span className="t-icon-close" />
                      </IconButton>
                    ) : !currentCup ? (
                      <Typography
                        component="span"
                        className="t-custom-dropdown t-icon-arrow-down"
                        sx={{
                          color: "primary.main",
                          position: "absolute",
                          right: "16px",
                          pointerEvents: "none",
                        }}
                      />
                    ) : null}
                  </>
                ),
              }}
            />
          )}
        />
      </FormControl>
    </FormControl>
  );
};

export default ConsumptionsManagementsDeliveryAddressComponent;
